import { useState, useCallback, useMemo, useEffect, useRef, type PointerEvent as ReactPointerEvent, type WheelEvent as ReactWheelEvent } from 'react';
import { cn } from '@/lib/utils';
import { RotateCcw, Plus, Trash2, Link2, Pencil, Maximize2, Minimize2, Layers, Folder, Settings2, Sun, Moon, Clock3, Orbit, CalendarDays, Palette, Sparkles, CircleDot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Slider } from '@/components/ui/slider';
import { MoonPhase } from './MoonPhase';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import type { CycleGroupData } from '@/lib/cycleGroupStore';
import type { CycleRing } from '@/lib/cycleTypes';
import { loadCycleGroups, saveCycleGroup, subscribeCycleGroups, deleteCycleGroup, saveSelectedGroupId, loadSelectedGroupId, subscribeSelectedGroup } from '@/lib/cycleGroupStore';
import { toast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';

// Synodic month for lunar calculations
const SYNODIC_MONTH = 29.530588853;
const KNOWN_NEW_MOON = new Date('2012-12-13T08:42:00Z').getTime();
const ZERO_REFERENCE_OFFSET_DEG = 90;
const ICON_LABEL_REGEX = /\p{Extended_Pictographic}/u;

const isInteractiveRailTarget = (target: EventTarget | null) => target instanceof Element && Boolean(target.closest('button, [role="button"], a, input, select, textarea, label'));

// Calculate moon illumination for a given day of year (1-364)
function getMoonIlluminationForDay(dayOfYear: number, referenceDate: Date): number {
  // Create date from day of year (galactic year starts July 26)
  const galacticYearStart = new Date(referenceDate);
  galacticYearStart.setMonth(6, 26); // July 26
  galacticYearStart.setHours(0, 0, 0, 0);

  // If we're before the current galactic year start, use the previous year start
  if (referenceDate < galacticYearStart) {
    galacticYearStart.setFullYear(galacticYearStart.getFullYear() - 1);
  }

  const startDate = new Date(galacticYearStart);
  startDate.setDate(startDate.getDate() + dayOfYear - 1);
  
  const daysSinceNewMoon = (startDate.getTime() - KNOWN_NEW_MOON) / (1000 * 60 * 60 * 24);
  let phase = (daysSinceNewMoon % SYNODIC_MONTH) / SYNODIC_MONTH;
  if (phase < 0) phase += 1;
  
  // Illumination: 0 at new moon, 1 at full moon
  return (1 - Math.cos(phase * 2 * Math.PI)) / 2;
}

interface MultiRingProps {
  viewId?: string | null;
  rings: CycleRing[];
  hiddenRings?: string[];
  onToggleRing?: (ringId: string) => void;
  hideToolbar?: boolean;
  hideRail?: boolean;
  railOrientation?: 'vertical' | 'horizontal';
  dashboardEditing?: boolean;
  onRingsChange?: (rings: CycleRing[]) => void;
  onAddCycle?: () => void;
  onRemoveCycle?: (id: string) => void;
  onEditCycle?: (ring: CycleRing) => void;
  onOpenAlignment?: (initialCycleId?: string) => void;
  toolbarActions?: {
    openWizard?: () => void;
    openTheme?: () => void;
    openAlignment?: (initial?: string) => void;
  };
  alignments?: Array<{
    sourceCycleId: string;
    targetCycleId: string;
    sourceSegments?: number;
    targetSegments?: number;
  }>;
  onSyncToNow?: () => void;
  onRotationChange?: (dayOfYear: number, fractionOfDay: number) => void;
  gregorianDate?: Date;
  maxRings?: number;
  ringThemeLayout?: {
    ringWidth?: number;
    ringGap?: number;
    visibleRingCount?: number;
  };
  displayMode?: 'single' | 'multi';
  onDisplayModeChange?: (mode: 'single' | 'multi') => void;
  onVisibleCountChange?: (count: number) => void;
  onInteractionEnd?: () => void;
  constrainToContainer?: boolean;
}

export function MultiRing({ 
  viewId,
  rings, 
  hideToolbar = false,
  hideRail = false,
  railOrientation = 'vertical',
  onRingsChange, 
  onAddCycle,
  onRemoveCycle,
  onEditCycle,
  onOpenAlignment,
  toolbarActions,
  dashboardEditing = false,
  alignments = [],
  onSyncToNow,
  onRotationChange,
  gregorianDate,
  hiddenRings = [],
  onToggleRing,
  maxRings = 32, // allow way more rings by default
  ringThemeLayout,
  displayMode = 'multi',
  onDisplayModeChange,
  onVisibleCountChange,
  onInteractionEnd,
  constrainToContainer = false,
}: MultiRingProps) {
  // --- State and refs ---
  const FIT_PADDING = 12;
  const [visibleRingCount, setVisibleRingCount] = useState(() => {
    // Use ringThemeLayout.visibleRingCount if provided, otherwise default to MIN_MOBILE_RINGS minimum
    if (ringThemeLayout?.visibleRingCount !== undefined) {
      return Math.min(maxRings, ringThemeLayout.visibleRingCount, rings.length);
    }
    return Math.min(maxRings, rings.length);
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [dragging, setDragging] = useState(null);
  const [selectedRing, setSelectedRing] = useState<string | null>(null);
  const [showAddCycle, setShowAddCycle] = useState(false);
  const [cycleGroups, setCycleGroups] = useState<CycleGroupData[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(() => loadSelectedGroupId(viewId));
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [groupDraft, setGroupDraft] = useState({ name: '', description: '' });
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [isCycleOverlayActive, setIsCycleOverlayActive] = useState(false);
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const overlayHideTimerRef = useRef<number | null>(null);
  const horizontalRailRef = useRef<HTMLDivElement | null>(null);
  const lastRotatedRingsRef = useRef<typeof safeRings | null>(null);
  const liveEmitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const railDragStateRef = useRef<{ startX: number; startScrollLeft: number; dragging: boolean }>({
    startX: 0,
    startScrollLeft: 0,
    dragging: false,
  });
  const selectedGroupIdRef = useRef(null);
  const isMobile = useIsMobile();

  const safeRings = useMemo(() =>
    (Array.isArray(rings) ? rings : [])
      .filter((ring): ring is CycleRing => Boolean(ring && ring.id))
      .map((ring) => ({
        ...ring,
        name: ring?.name ?? 'Unnamed',
        stages: ring?.stages ?? 1,
        stagesPerParent: ring?.stagesPerParent ?? 1,
      })),
  [rings]);

  const safeCycleGroups = useMemo(() =>
    (Array.isArray(cycleGroups) ? cycleGroups : [])
      .filter((group): group is CycleGroupData => Boolean(group && group.id))
      .map((group) => ({
        id: group.id,
        name: group?.name ?? 'Unnamed Group',
        description: group?.description,
        rings: Array.isArray(group?.rings) ? group.rings : [],
        hiddenRings: group?.hiddenRings ?? [],
        visibleRingCount: group?.visibleRingCount,
      })),
  [cycleGroups]);

  // Always keep visibleRingCount in sync with rings/maxRings
  useEffect(() => {
    setVisibleRingCount((prev) => {
      // Respect ringThemeLayout.visibleRingCount if provided, otherwise show all rings
      let next: number;
      if (ringThemeLayout?.visibleRingCount !== undefined) {
        next = Math.min(maxRings, ringThemeLayout.visibleRingCount, safeRings.length);
      } else {
        next = Math.min(maxRings, safeRings.length);
      }
      if (next !== prev) {
        try {
          if (typeof onVisibleCountChange === 'function') onVisibleCountChange(next);
        } catch {
          // ignore
        }
      }
      return next;
    });
  }, [safeRings.length, maxRings, ringThemeLayout?.visibleRingCount]);

  useEffect(() => {
    selectedGroupIdRef.current = selectedGroupId;
  }, [selectedGroupId]);

  useEffect(() => {
    let active = true;
    const handleGroups = (groups: CycleGroupData[]) => {
      if (!active) return;
      setCycleGroups(groups);
    };

    const unsubscribeGroups = subscribeCycleGroups(viewId, handleGroups);
    if (!unsubscribeGroups) {
      loadCycleGroups(viewId).then(handleGroups).catch(() => undefined);
    }

    const unsubscribeSelected = subscribeSelectedGroup(viewId, (id) => {
      if (!active) return;
      setSelectedGroupId(id);
    });

    return () => {
      active = false;
      unsubscribeGroups?.();
      unsubscribeSelected?.();
    };
  }, [viewId]);

  // notify parent when visible count changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (typeof onVisibleCountChange === 'function') {
      try {
        onVisibleCountChange(visibleRingCount);
      } catch {
        void 0;
      }
    }
  }, [visibleRingCount]);
  
  // Predefined order for core rings — lower = OUTER ring (largest radius)
  const RING_ORDER: Record<string, number> = {
    lunar: 0,            // outermost
    moons: 1,
    days: 2,
    plasmas: 3,
    colors: 4,
    seals: 5,
    families: 6,
    quarters: 7,
    jupiter: 8,
    'gregorian-leap': 9,
    sun: 10,
    solarSecond: 11,
    solarMinute: 12,
    solarHour: 13,
    saturn: 20,
    mercury: 21,
    venus: 22,
    mars: 23,
    weeks: 24,
    tzolkin: 25,
  };
  
  // Sort rings by displayOrder to maintain consistent positions
  // Lower displayOrder = OUTER ring (rendered at index 0, largest radius), higher = inner
  // Use predefined order as fallback for rings without displayOrder
  const sortedRings = [...safeRings].sort((a, b) => {
    const orderA = a.displayOrder ?? RING_ORDER[a.id] ?? 999;
    const orderB = b.displayOrder ?? RING_ORDER[b.id] ?? 999;
    return orderA - orderB;
  });
  
  const baseRings = Array.isArray(hiddenRings) && hiddenRings.length
    ? sortedRings.filter(r => !hiddenRings.includes(r.id))
    : sortedRings;
  const effectiveDisplayMode = displayMode === 'single' ? 'single' : 'multi';
  const singleModeRings = baseRings.length > 0 ? baseRings : sortedRings;
  const selectedVisibleRing = useMemo(() => {
    if (!singleModeRings.length) return null;
    return singleModeRings.find((ring) => ring.id === selectedRing) ?? singleModeRings[0];
  }, [singleModeRings, selectedRing]);
  const singleRingIconCount = useMemo(() => {
    if (!selectedVisibleRing) return 0;
    const stages = Math.max(1, selectedVisibleRing.stages ?? 1);
    if (stages === 28) return 28;
    if (stages <= 12) return stages;
    if (stages <= 20) return 12;
    if (stages <= 40) return 16;
    return 20;
  }, [selectedVisibleRing]);
  const displayedRings = effectiveDisplayMode === 'single'
    ? (selectedVisibleRing ? [selectedVisibleRing] : [])
    : baseRings.slice(0, visibleRingCount);
  const isSingleRing = displayedRings.length === 1;
  const totalAvailableRings = safeRings.length;
  const railRings = useMemo(
    () => sortedRings.filter((ring) => ring && ring.id && !(Array.isArray(hiddenRings) && hiddenRings.includes(ring.id))),
    [sortedRings, hiddenRings],
  );
  const useSplitVerticalRails = !hideRail && railRings.length > 1 && railOrientation === 'horizontal';
  const useVerticalRail = !hideRail && railRings.length > 1 && !useSplitVerticalRails;
  const useOverlayRails = useVerticalRail || useSplitVerticalRails;
  const shouldAlwaysShowOverlayRails = isMobile || constrainToContainer;
  const shouldShowOverlayRails = shouldAlwaysShowOverlayRails || isCycleOverlayActive;
  const splitRailIndex = Math.ceil(railRings.length / 2);
  const leftRailRings = useMemo(() => (useSplitVerticalRails ? railRings.slice(0, splitRailIndex) : railRings), [railRings, splitRailIndex, useSplitVerticalRails]);
  const rightRailRings = useMemo(() => (useSplitVerticalRails ? railRings.slice(splitRailIndex) : []), [railRings, splitRailIndex, useSplitVerticalRails]);

  const activateCycleOverlay = useCallback(() => {
    if (overlayHideTimerRef.current) {
      window.clearTimeout(overlayHideTimerRef.current);
      overlayHideTimerRef.current = null;
    }
    setIsCycleOverlayActive(true);
  }, []);

  const scheduleHideCycleOverlay = useCallback(() => {
    if (shouldAlwaysShowOverlayRails) return;
    if (overlayHideTimerRef.current) {
      window.clearTimeout(overlayHideTimerRef.current);
    }
    overlayHideTimerRef.current = window.setTimeout(() => {
      setIsCycleOverlayActive(false);
      overlayHideTimerRef.current = null;
    }, 120);
  }, [shouldAlwaysShowOverlayRails]);

  useEffect(() => {
    return () => {
      if (overlayHideTimerRef.current) {
        window.clearTimeout(overlayHideTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!baseRings.length) {
      setSelectedRing(null);
      return;
    }
    setSelectedRing((prev) => (
      prev && baseRings.some((ring) => ring.id === prev) ? prev : baseRings[0].id
    ));
  }, [baseRings]);

  const getRailIcon = useCallback((ringId: string) => {
    const id = (ringId || '').toLowerCase();
    if (id.includes('moon') || id.includes('lunar')) return Moon;
    if (id.includes('sun') || id.includes('day')) return Sun;
    if (id.includes('hour') || id.includes('minute') || id.includes('second')) return Clock3;
    if (id.includes('color')) return Palette;
    if (id.includes('plasma') || id.includes('seal') || id.includes('family') || id.includes('quarter')) return Sparkles;
    if (id.includes('jupiter') || id.includes('saturn') || id.includes('mercury') || id.includes('venus') || id.includes('mars')) return Orbit;
    if (id.includes('week') || id.includes('tzolkin') || id.includes('gregorian')) return CalendarDays;
    return CircleDot;
  }, []);
  const singleRingDisplayLabels = useMemo(() => {
    if (!selectedVisibleRing) return [] as string[];
    const totalStages = Math.max(1, selectedVisibleRing.stages ?? 1);
    const labels = Array.isArray(selectedVisibleRing.labels) ? selectedVisibleRing.labels : [];
    const targetCount = Math.max(1, singleRingIconCount);
    const stageLabels = Array.from({ length: totalStages }, (_, stageIndex) => {
      const raw = labels[stageIndex];
      return typeof raw === 'string' ? raw.trim() : '';
    });

    const iconLabels = stageLabels.filter((label) => label.length > 0 && ICON_LABEL_REGEX.test(label));
    const hasIcons = iconLabels.length > 0;
    const allIconsSame = hasIcons && new Set(iconLabels).size === 1;
    const shouldUseNumericFallback = !hasIcons || allIconsSame;

    const getStageDisplayValue = (stageIndex: number) => {
      if (shouldUseNumericFallback) return String(stageIndex + 1);
      const iconLabel = stageLabels[stageIndex];
      return iconLabel.length > 0 ? iconLabel : String(stageIndex + 1);
    };

    if (targetCount >= totalStages) {
      return Array.from({ length: totalStages }, (_, stageIndex) => getStageDisplayValue(stageIndex));
    }

    return Array.from({ length: targetCount }, (_, sampleIndex) => {
      const stageIndex = Math.floor((sampleIndex * totalStages) / targetCount);
      return getStageDisplayValue(stageIndex);
    });
  }, [selectedVisibleRing, singleRingIconCount]);

  const ringHasIconLabels = useCallback((ring: CycleRing) => {
    if (!Array.isArray(ring.labels) || ring.labels.length === 0) return false;
    return ring.labels.some((label) => typeof label === 'string' && ICON_LABEL_REGEX.test(label.trim()));
  }, []);

  const selectRailRing = useCallback((ringId: string, index: number) => {
    setSelectedRing(ringId);
    if (effectiveDisplayMode === 'multi') {
      const targetVisibleCount = Math.min(maxRings, index + 1);
      if (targetVisibleCount > visibleRingCount) {
        setVisibleRingCount(targetVisibleCount);
        if (typeof onVisibleCountChange === 'function') {
          try { onVisibleCountChange(targetVisibleCount); } catch { void 0; }
        }
      }
    }
  }, [effectiveDisplayMode, maxRings, onVisibleCountChange, visibleRingCount]);

  const handleRailPress = useCallback((ringId: string, index: number) => {
    if (onToggleRing) {
      const isHidden = hiddenRings.includes(ringId);
      onToggleRing(ringId);
      if (isHidden) {
        // When re-enabling a ring, also select it so users can see it immediately.
        selectRailRing(ringId, index);
      }
      return;
    }
    selectRailRing(ringId, index);
  }, [hiddenRings, onToggleRing, selectRailRing]);

  const handleHorizontalRailPointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const rail = horizontalRailRef.current;
    if (!rail) return;
    if (isInteractiveRailTarget(event.target)) {
      railDragStateRef.current.dragging = false;
      return;
    }
    railDragStateRef.current = {
      startX: event.clientX,
      startScrollLeft: rail.scrollLeft,
      dragging: false,
    };
    rail.setPointerCapture(event.pointerId);
  }, []);
  
  const handleHorizontalRailPointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const rail = horizontalRailRef.current;
    if (!rail || !rail.hasPointerCapture(event.pointerId)) return;
    const deltaX = event.clientX - railDragStateRef.current.startX;
    if (!railDragStateRef.current.dragging && Math.abs(deltaX) > 6) {
      railDragStateRef.current.dragging = true;
    }
    rail.scrollLeft = railDragStateRef.current.startScrollLeft - deltaX;
  }, []);
  
  const handleHorizontalRailPointerUp = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const rail = horizontalRailRef.current;
    if (!rail) return;
    if (rail.hasPointerCapture(event.pointerId)) {
      rail.releasePointerCapture(event.pointerId);
    }
    window.setTimeout(() => {
      railDragStateRef.current.dragging = false;
    }, 0);
  }, []);
  
  const handleHorizontalRailWheel = useCallback((event: ReactWheelEvent<HTMLDivElement>) => {
    const rail = horizontalRailRef.current;
    if (!rail) return;
    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
    rail.scrollLeft += event.deltaY;
    event.preventDefault();
  }, []);
  
  // Dynamic sizing based on fullscreen mode and container measurements
  const [computedSize, setComputedSize] = useState<number | null>(null);
  useEffect(() => {
    if (isFullscreen) {
      setComputedSize(Math.max(220, Math.min(window.innerWidth, window.innerHeight) - 120));
      return;
    }
    const el = canvasRef.current ?? wrapperRef.current;
    if (!el) {
      setComputedSize(360);
      return;
    }

    const update = () => {
      const w = el.clientWidth || 360;
      const h = el.clientHeight || 360;
      const minSize = isMobile ? 140 : 180;
      const s = Math.max(minSize, Math.min(w, h) - FIT_PADDING * 2);
      setComputedSize(s);
    };

    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, [isFullscreen, isMobile, wrapperRef]);
  const size = computedSize ?? 360;
  const center = size / 2;
  // Dynamically thicken rings as fewer are shown - more aggressive scaling
  // With fewer rings, use much more space per ring
  const defaultRingWidth = displayedRings.length === 0
    ? 8
    : displayedRings.length === 1
      ? 32
      : displayedRings.length === 2
        ? 28
        : displayedRings.length <= 4
          ? 24
          : Math.max(8, 28 - Math.max(0, displayedRings.length - 5) * 1.5);
  const ringWidth = ringThemeLayout?.ringWidth
    ? Math.max(8, Math.min(30, ringThemeLayout.ringWidth))
    : defaultRingWidth;
  const ringGap = ringThemeLayout?.ringGap
    ? Math.max(0.5, Math.min(6, ringThemeLayout.ringGap))
    : displayedRings.length > 10
      ? 1
      : displayedRings.length > 7
        ? 1.4
        : 1.8;
  const innerRadius = isFullscreen ? size * 0.12 : Math.min(45, Math.max(22, size * 0.24));
  const singleRingOuterR = center - 10;
  const singleRingInnerR = singleRingOuterR - ringWidth;
  const singleRingMidR = (singleRingOuterR + singleRingInnerR) / 2;
  
  // Pre-calculate moon illumination for all 364 days
  const moonIlluminations = useMemo(() => {
    const referenceDate = gregorianDate || new Date();
    return Array.from({ length: 364 }, (_, i) => getMoonIlluminationForDay(i + 1, referenceDate));
  }, [gregorianDate]);
  
  const createArcPath = (
    startAngle: number,
    endAngle: number,
    innerR: number,
    outerR: number
  ) => {
    const startRad = (startAngle + ZERO_REFERENCE_OFFSET_DEG) * (Math.PI / 180);
    const endRad = (endAngle + ZERO_REFERENCE_OFFSET_DEG) * (Math.PI / 180);
    
    const x1 = center + outerR * Math.cos(startRad);
    const y1 = center + outerR * Math.sin(startRad);
    const x2 = center + outerR * Math.cos(endRad);
    const y2 = center + outerR * Math.sin(endRad);
    const x3 = center + innerR * Math.cos(endRad);
    const y3 = center + innerR * Math.sin(endRad);
    const x4 = center + innerR * Math.cos(startRad);
    const y4 = center + innerR * Math.sin(startRad);
    
    // Handle both clockwise and counter-clockwise directions
    const angleDiff = endAngle - startAngle;
    const largeArc = Math.abs(angleDiff) > 180 ? 1 : 0;
    const sweepOuter = angleDiff > 0 ? 1 : 0;
    const sweepInner = angleDiff > 0 ? 0 : 1;
    
    return `M ${x1} ${y1} A ${outerR} ${outerR} 0 ${largeArc} ${sweepOuter} ${x2} ${y2} L ${x3} ${y3} A ${innerR} ${innerR} 0 ${largeArc} ${sweepInner} ${x4} ${y4} Z`;
  };

  const getAngleFromPoint = useCallback((clientX: number, clientY: number, rect: DOMRect) => {
    const x = clientX - rect.left - center;
    const y = clientY - rect.top - center;
    return Math.atan2(y, x) * (180 / Math.PI) - ZERO_REFERENCE_OFFSET_DEG;
  }, [center]);

  // Get the active stage - the one that appears at the top (heading 0)
  // With negative rotation, we rotate the ring so currentStage is at top
  // The stage at top = -rotation / segmentAngle
  const getActiveStage = (rotation: number, stages: number) => {
    const segmentAngle = 360 / stages;
    const normalizedRotation = (((-rotation) % 360) + 360) % 360;
    // Floor to avoid rounding drift between ring display and widgets
    return Math.floor(normalizedRotation / segmentAngle + 1e-6) % stages;
  };

  // Handle rotation with explicit parent-child linking
  // Master ring (being dragged) moves freely with deltaAngle
  // Slave rings follow proportionally based on their stage ratios
  const handleRotate = useCallback((ringId: string, deltaAngle: number) => {
    setSelectedRing(ringId);
    const draggedRing = safeRings.find(r => r.id === ringId);
    if (!draggedRing) return;

    // Build a map of all rings by id for quick lookup
    const ringMap = new Map<string, CycleRing>(safeRings.map(r => [r.id, r]));
    
    // Calculate cumulative rotation ratio between any two rings using their stage definitions.
    // Each hop respects the declared parent-child relationship:
    // - Moving from parent to child multiplies by how many child segments advance per parent segment,
    //   normalized by segment sizes.
    // - Moving from child to parent divides by that same relationship.
    const getChainRatio = (fromId: string, toId: string): number | null => {
      if (fromId === toId) return 1;

      const visited = new Set<string>();
      const queue: Array<{ id: string; ratio: number }> = [{ id: fromId, ratio: 1 }];

      const getAlignmentRotationRatio = (
        fromRing: CycleRing,
        toRing: CycleRing,
        fromSegments: number,
        toSegments: number,
      ) => {
        if (fromSegments <= 0 || toSegments <= 0 || fromRing.stages <= 0 || toRing.stages <= 0) {
          return null;
        }
        return (toSegments * fromRing.stages) / (fromSegments * toRing.stages);
      };

      const directAlignment = alignments.find(
        alignment =>
          (alignment.sourceCycleId === fromId && alignment.targetCycleId === toId) ||
          (alignment.sourceCycleId === toId && alignment.targetCycleId === fromId)
      );

      if (directAlignment && directAlignment.sourceSegments && directAlignment.targetSegments) {
        const fromRing = ringMap.get(fromId);
        const toRing = ringMap.get(toId);
        if (fromRing && toRing) {
          const fromSegments = directAlignment.sourceCycleId === fromId
            ? directAlignment.sourceSegments
            : directAlignment.targetSegments;
          const toSegments = directAlignment.sourceCycleId === fromId
            ? directAlignment.targetSegments
            : directAlignment.sourceSegments;
          const alignmentRatio = getAlignmentRotationRatio(fromRing, toRing, fromSegments, toSegments);
          if (alignmentRatio !== null) {
            return alignmentRatio;
          }
        }
      }

      while (queue.length > 0) {
        const currentNode = queue.shift();
        if (!currentNode) break;
        const { id, ratio } = currentNode;

        if (visited.has(id)) continue;
        visited.add(id);

        if (id === toId) return ratio;

        const currentRing = ringMap.get(id);
        if (!currentRing) continue;

        for (const alignment of alignments) {
          const isSource = alignment.sourceCycleId === id;
          const isTarget = alignment.targetCycleId === id;

          if (!isSource && !isTarget) continue;
          if (!alignment.sourceSegments || !alignment.targetSegments) continue;

          const otherId = isSource ? alignment.targetCycleId : alignment.sourceCycleId;
          const otherRing = ringMap.get(otherId);
          if (!otherRing) continue;

          const fromSegments = isSource ? alignment.sourceSegments : alignment.targetSegments;
          const toSegments = isSource ? alignment.targetSegments : alignment.sourceSegments;
          const alignmentRatio = getAlignmentRotationRatio(currentRing, otherRing, fromSegments, toSegments);

          if (alignmentRatio !== null) {
            queue.push({ id: otherId, ratio: ratio * alignmentRatio });
          }
        }

        // Traverse up to parent
        if (currentRing.parentId && currentRing.stagesPerParent) {
          const parentRing = ringMap.get(currentRing.parentId);
          if (parentRing && parentRing.stages > 0 && currentRing.stages > 0) {
            const parentRatio = ratio * (currentRing.stages / (currentRing.stagesPerParent * parentRing.stages));
            queue.push({ id: parentRing.id, ratio: parentRatio });
          }
        }

        // Traverse down to children
        for (const child of safeRings) {
          if (child.parentId === id && child.stagesPerParent) {
            const currentRingStages = currentRing.stages || 1;
            if (child.stages > 0) {
              const childRatio = ratio * (child.stagesPerParent * currentRingStages / child.stages);
              queue.push({ id: child.id, ratio: childRatio });
            }
          }
        }
      }

      return null;
    };

    const newRings = safeRings.map((ring) => {
      // Master ring - direct rotation, no modifications
      if (ring.id === ringId) {
        return { ...ring, rotation: ring.rotation + deltaAngle };
      }

      // Slave rings - calculate proportional rotation using declared relationships
      const slaveRatio = getChainRatio(draggedRing.id, ring.id);

      if (slaveRatio !== null) {
        return { ...ring, rotation: ring.rotation + deltaAngle * slaveRatio };
      }

      return ring;
    });

    onRingsChange?.(newRings);
    // Store latest rings so we can emit onRotationChange once on drag release
    lastRotatedRingsRef.current = newRings;
  }, [alignments, safeRings, onRingsChange]);

  // Compute galactic day/fraction from a ring snapshot — called once on drag release
  const emitRotationChangeFromRings = useCallback((rings: typeof safeRings) => {
    if (!onRotationChange) return;
    const daysRing = rings.find(r => r.id === 'days');
    const moonsRing = rings.find(r => r.id === 'moons');
    if (!daysRing || !moonsRing) return;

    const dayStage = getActiveStage(daysRing.rotation, daysRing.stages);
    const monthStage = getActiveStage(moonsRing.rotation, moonsRing.stages);
    const segmentAngle = 360 / daysRing.stages;
    const normalizedDays = (((-daysRing.rotation) % 360) + 360) % 360;
    let fractionOfDay = (normalizedDays - dayStage * segmentAngle) / segmentAngle;

    const hourRing = rings.find(r => r.id === 'solarHour' || r.id === 'sun');
    const minuteRing = rings.find(r => r.id === 'solarMinute');
    const secondRing = rings.find(r => r.id === 'solarSecond');

    if (hourRing) {
      const hourStages = hourRing.stages || 24;
      const normalizedHour = (((-hourRing.rotation) % 360) + 360) % 360;
      const hourSegment = 360 / hourStages;
      const hourIndex = Math.floor(normalizedHour / hourSegment);
      const hourFrac = (normalizedHour - hourIndex * hourSegment) / hourSegment;
      let totalHours = hourIndex + hourFrac;
      if (minuteRing) {
        const minuteStages = minuteRing.stages || 60;
        const normalizedMin = (((-minuteRing.rotation) % 360) + 360) % 360;
        const minSegment = 360 / minuteStages;
        const minIndex = Math.floor(normalizedMin / minSegment);
        const minFrac = (normalizedMin - minIndex * minSegment) / minSegment;
        totalHours += (minIndex + minFrac) / 60;
      }
      if (secondRing) {
        const secStages = secondRing.stages || 60;
        const normalizedSec = (((-secondRing.rotation) % 360) + 360) % 360;
        const secSegment = 360 / secStages;
        const secIndex = Math.floor(normalizedSec / secSegment);
        const secFrac = (normalizedSec - secIndex * secSegment) / secSegment;
        totalHours += ((secIndex + secFrac) / 60) / 60;
      }
      fractionOfDay = Math.max(0, Math.min(1, totalHours / 24));
    }

    const dayInMonth = dayStage + 1;
    const month = monthStage + 1;
    const dayOfYear = (month - 1) * 28 + dayInMonth;
    onRotationChange(dayOfYear, fractionOfDay);
  }, [getActiveStage, onRotationChange]);

  // Reset all rotations to 0
  const resetRotations = useCallback(() => {
    const newRings = safeRings.map(ring => ({ ...ring, rotation: 0 }));
    onRingsChange?.(newRings);
  }, [safeRings, onRingsChange]);

  const handleMouseDown = (e: React.MouseEvent<SVGGElement>, ringId: string) => {
    const rect = e.currentTarget.closest('svg')?.getBoundingClientRect();
    if (!rect) return;
    const startAngle = getAngleFromPoint(e.clientX, e.clientY, rect);
    const ring = safeRings.find(r => r.id === ringId);
    lastRotatedRingsRef.current = null;
    setDragging({ ringId, startAngle, lastRotation: ring?.rotation || 0 });
    setSelectedRing(ringId);
  };

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (!dragging) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const currentAngle = getAngleFromPoint(e.clientX, e.clientY, rect);
    let deltaAngle = currentAngle - dragging.startAngle;
    if (deltaAngle > 180) deltaAngle -= 360;
    if (deltaAngle < -180) deltaAngle += 360;
    handleRotate(dragging.ringId, deltaAngle);
    setDragging({ ...dragging, startAngle: currentAngle });
    // Live-emit every frame so header/waveform update in real time
    const rings = lastRotatedRingsRef.current;
    if (rings) emitRotationChangeFromRings(rings);
  }, [dragging, emitRotationChangeFromRings, getAngleFromPoint, handleRotate]);

  // Touch drag support
  const handleTouchStart = (e: React.TouchEvent<SVGSVGElement>) => {
    if (e.touches.length !== 1) return;
    const touch = e.touches[0];
    const rect = e.currentTarget.getBoundingClientRect();
    const startAngle = getAngleFromPoint(touch.clientX, touch.clientY, rect);
    const ring = displayedRings[displayedRings.length - 1] ?? safeRings[safeRings.length - 1];
    if (!ring) return;
    lastRotatedRingsRef.current = null;
    setDragging({ ringId: ring.id, startAngle, lastRotation: ring.rotation || 0 });
    setSelectedRing(ring.id);
  };

  const handleTouchMove = (e: React.TouchEvent<SVGSVGElement>) => {
    if (!dragging || e.touches.length !== 1) return;
    const touch = e.touches[0];
    const rect = e.currentTarget.getBoundingClientRect();
    const currentAngle = getAngleFromPoint(touch.clientX, touch.clientY, rect);
    let deltaAngle = currentAngle - dragging.startAngle;
    if (deltaAngle > 180) deltaAngle -= 360;
    if (deltaAngle < -180) deltaAngle += 360;
    const ring = safeRings.find(r => r.id === dragging.ringId);
    if (isMobile && ring && ring.id === 'solarMinute') {
      const segmentAngle = 360 / ring.stages;
      const targetRotation = ring.rotation + deltaAngle;
      const snappedRotation = Math.round(targetRotation / segmentAngle) * segmentAngle;
      deltaAngle = snappedRotation - ring.rotation;
    }
    handleRotate(dragging.ringId, deltaAngle);
    setDragging({ ...dragging, startAngle: currentAngle });
    // Live-emit every frame so header/waveform update in real time
    const rings = lastRotatedRingsRef.current;
    if (rings) emitRotationChangeFromRings(rings);
  };

  const handleTouchEnd = () => {
    const finalRings = lastRotatedRingsRef.current ?? safeRings;
    lastRotatedRingsRef.current = null;
    setDragging(null);
    emitRotationChangeFromRings(finalRings);
    if (onInteractionEnd) {
      onInteractionEnd();
    }
  };

  const handleMouseUp = () => {
    const finalRings = lastRotatedRingsRef.current ?? safeRings;
    lastRotatedRingsRef.current = null;
    setDragging(null);
    emitRotationChangeFromRings(finalRings);
    if (onInteractionEnd) {
      onInteractionEnd();
    }
  };

  const handleStageClick = (ringId: string, stageIndex: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const ring = safeRings.find(r => r.id === ringId);
    if (!ring) return;
    
    const segmentAngle = 360 / ring.stages;
    const currentActiveStage = getActiveStage(ring.rotation, ring.stages);
    
    // Calculate shortest path to target stage
    let stagesDiff = stageIndex - currentActiveStage;
    
    // Normalize to shortest path (-half to +half of total stages)
    const halfStages = ring.stages / 2;
    while (stagesDiff > halfStages) stagesDiff -= ring.stages;
    while (stagesDiff < -halfStages) stagesDiff += ring.stages;
    
    const deltaRotation = -stagesDiff * segmentAngle;
    handleRotate(ringId, deltaRotation);
    if (onInteractionEnd) {
      onInteractionEnd();
    }
  };

  // Toggle fullscreen
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  // Handle escape key to exit fullscreen
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && isFullscreen) {
      setIsFullscreen(false);
    }
  }, [isFullscreen]);

  const toggleDisplayMode = useCallback(() => {
    const nextMode = effectiveDisplayMode === 'single' ? 'multi' : 'single';
    if (nextMode === 'single' && selectedVisibleRing) {
      setSelectedRing(selectedVisibleRing.id);
    }
    onDisplayModeChange?.(nextMode);
  }, [effectiveDisplayMode, onDisplayModeChange, selectedVisibleRing]);

  // Add/remove escape key listener
  useMemo(() => {
    if (isFullscreen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isFullscreen, handleKeyDown]);

  const containerClasses = isFullscreen
    ? "fixed inset-0 z-50 bg-background/95 backdrop-blur-md flex items-center justify-center"
    : "relative w-full h-full";

  return (
    <div className={containerClasses}>
      {/* Fullscreen backdrop click to close */}
      {isFullscreen && (
        <div 
          className="absolute inset-0" 
          onClick={() => setIsFullscreen(false)}
        />
      )}
      {/* Vertically center and stretch MultiRing for better widget fit */}
      <div ref={wrapperRef} className={cn(
        "relative flex flex-col items-center",
        // In page mode: fit to screen, full width, max 100vw, center, with padding
        !isFullscreen && (constrainToContainer
          ? "w-full max-w-full h-full min-h-0 overflow-hidden py-1 px-1"
          : (isMobile
            ? `w-full max-w-full h-full ${hideToolbar ? 'min-h-[340px]' : 'min-h-[240px]'} overflow-hidden py-1 px-1`
            : "w-full max-w-full h-full min-h-[320px] overflow-hidden py-2 px-2")),
        isFullscreen && "w-full h-full",
        displayedRings.length > 16 && "text-[0.85em]"
      )} style={{ minHeight: (isFullscreen || constrainToContainer) ? undefined : (isMobile ? (hideToolbar ? 340 : 240) : 320) }}>
        {/* Controls */}
        {!hideToolbar ? (
        <div
          className={cn(
            "w-full flex items-center gap-2 px-1 z-10",
            isMobile ? "flex-nowrap overflow-x-auto pb-1 mb-2" : "flex-wrap justify-between mb-3"
          )}
          onMouseEnter={() => setShowAddCycle(true)}
          onMouseLeave={() => setShowAddCycle(false)}
        >
          <div className={cn("flex flex-col gap-1", isMobile && "min-w-max")}>
            <div className="flex gap-1 items-center">
              {dashboardEditing && onAddCycle && showAddCycle && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={onAddCycle}
                      onFocus={() => setShowAddCycle(true)}
                      onBlur={() => setShowAddCycle(false)}
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Add cycle</TooltipContent>
                </Tooltip>
              )}

              {/* Cycle groups selector + add/save controls */}
              <Popover>
                <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7">
                      <Folder className="w-3.5 h-3.5" />
                    </Button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent>Groups</TooltipContent>
              </Tooltip>
              <PopoverContent className="w-72 p-3" side="bottom" align="start">
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Choose group
                    </Label>
                    <Select value={selectedGroupId || undefined} onValueChange={(v) => {
                      const id = v || '';
                      setSelectedGroupId(id);
                      try { saveSelectedGroupId(viewId, id || null); } catch { void 0; }
                    }}>
                      <SelectTrigger className="bg-card/60 backdrop-blur border-border/60">
                        <SelectValue placeholder="Select a group" />
                      </SelectTrigger>
                      <SelectContent className="bg-card/90 backdrop-blur border-border/60">
                        {safeCycleGroups.map((group) => (
                          <SelectItem key={group.id} value={group.id}>
                            {group.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {dashboardEditing && (
                    <div className="flex flex-col gap-2 mt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newId = `group-${Date.now()}`;
                          setEditingGroupId(newId);
                          setIsCreatingGroup(true);
                          setGroupDraft({ name: '', description: '' });
                          setShowGroupDialog(true);
                        }}
                      >
                        Create new group
                      </Button>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="flex-1"
                          onClick={() => {
                            let group = safeCycleGroups.find(g => g.id === selectedGroupId);
                            if (!group && safeCycleGroups.length > 0) {
                              group = safeCycleGroups[0];
                              setSelectedGroupId(group.id);
                            }
                            if (!group) return;
                            setEditingGroupId(group.id);
                            setIsCreatingGroup(false);
                            setGroupDraft({ name: group.name, description: group.description ?? '' });
                            setShowGroupDialog(true);
                          }}
                          disabled={safeCycleGroups.length === 0}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          className="flex-1"
                          onClick={async () => {
                            if (!selectedGroupId) return;
                            const confirmed = window.confirm('Delete this group? This cannot be undone.');
                            if (!confirmed) return;
                            try {
                              await deleteCycleGroup(viewId, selectedGroupId);
                              const updated = await loadCycleGroups(viewId);
                              setCycleGroups(updated);
                              const nextId = updated[0]?.id ?? null;
                              setSelectedGroupId(nextId ?? '');
                              try { saveSelectedGroupId(viewId, nextId); } catch { void 0; }
                              toast.success('Group deleted');
                            } catch {
                              toast.error('Failed to delete group');
                            }
                          }}
                          disabled={!selectedGroupId}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
              {/* Display selected group name for public view */}
              {selectedGroupId && (
                <div className={cn("ml-2 text-sm text-muted-foreground truncate max-w-[220px]", isMobile && "text-xs max-w-[120px]")}>
                  {safeCycleGroups.find(g => g.id === selectedGroupId)?.name ?? 'Group'}
                </div>
              )}
            </div>

            <div className="flex gap-1 items-center">
              {/* Ring count control */}
              <Popover>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 relative">
                      <Layers className="w-3.5 h-3.5" />
                      <span className="absolute -bottom-0.5 -right-0.5 text-[9px] font-bold bg-primary text-primary-foreground rounded-full w-3.5 h-3.5 flex items-center justify-center">
                        {visibleRingCount}
                      </span>
                    </Button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent>Visible rings</TooltipContent>
              </Tooltip>
              <PopoverContent className="w-48 p-3" side="bottom" align="end">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Visible Rings</span>
                    <span className="text-sm text-muted-foreground">{visibleRingCount} / {totalAvailableRings}</span>
                  </div>
                  <Slider
                    value={[visibleRingCount]}
                    onValueChange={([value]) => {
                      setVisibleRingCount(value);
                      if (typeof onVisibleCountChange === 'function') {
                        try { onVisibleCountChange(value); } catch { void 0; }
                      }
                    }}
                    min={1}
                    max={Math.min(maxRings, totalAvailableRings)}
                    step={1}
                    className="w-full"
                    disabled={effectiveDisplayMode === 'single'}
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>1</span>
                    <span>{Math.min(maxRings, totalAvailableRings)}</span>
                  </div>
                  {effectiveDisplayMode === 'single' && (
                    <p className="text-xs text-muted-foreground">
                      Single mode shows the ring selected in the rail.
                    </p>
                  )}
                </div>
              </PopoverContent>
            </Popover>
              <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={effectiveDisplayMode === 'single' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-7 px-2 gap-1.5"
                  onClick={toggleDisplayMode}
                >
                  {effectiveDisplayMode === 'single' ? (
                    <CircleDot className="w-3.5 h-3.5" />
                  ) : (
                    <Layers className="w-3.5 h-3.5" />
                  )}
                  <span>{effectiveDisplayMode === 'single' ? 'Single' : 'Multi'}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {effectiveDisplayMode === 'single'
                  ? 'Show one selected ring with stage markers'
                  : 'Show multiple visible rings'}
              </TooltipContent>
            </Tooltip>
            </div>
          </div>
          <div className={cn("flex gap-1", isMobile && "min-w-max")}>
            {toolbarActions?.openWizard && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => toolbarActions.openWizard?.()}
                  >
                    <Settings2 className="w-3.5 h-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Open cycle wizard</TooltipContent>
              </Tooltip>
            )}
            {onOpenAlignment && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => onOpenAlignment(selectedRing || undefined)}
                  >
                    <Link2 className="w-3.5 h-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Align cycles</TooltipContent>
              </Tooltip>
            )}
            {/* Fullscreen toggle */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-7 w-7"
                  onClick={toggleFullscreen}
                >
                  {isFullscreen ? (
                    <Minimize2 className="w-3.5 h-3.5" />
                  ) : (
                    <Maximize2 className="w-3.5 h-3.5" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={resetRotations}>
                  <RotateCcw className="w-3.5 h-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Reset rotations</TooltipContent>
            </Tooltip>
          </div>
        </div>
        ) : null}
      
        <div className={cn("group/multiring relative flex w-full min-w-0 flex-1 overflow-hidden", isMobile ? "items-center justify-center" : "items-center justify-center")}>
          {useOverlayRails && !useSplitVerticalRails ? (
            <div
              onMouseEnter={activateCycleOverlay}
              onMouseLeave={scheduleHideCycleOverlay}
              onFocusCapture={activateCycleOverlay}
              onBlurCapture={scheduleHideCycleOverlay}
              className="pointer-events-none absolute inset-y-0 left-0 z-20 flex items-center pl-1"
            >
              <div className="pointer-events-auto flex max-h-[min(70vh,520px)] w-9 flex-col gap-1 overflow-y-auto rounded-lg border border-border/50 bg-background/80 p-1 shadow-lg backdrop-blur-sm">
                {leftRailRings.map((ring, localIndex) => {
                  const index = localIndex;
                  const isHidden = hiddenRings.includes(ring.id);
                  const isSelected = !isHidden && (selectedRing === ring.id || (!selectedRing && index === 0));
                  const Icon = getRailIcon(ring.id);
                  return (
                    <Tooltip key={`ring-rail-${ring.id}`}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => handleRailPress(ring.id, index)}
                          className={cn(
                            "flex h-7 w-7 items-center justify-center rounded-md transition-colors duration-150",
                            isSelected
                              ? "bg-primary/18 text-primary"
                              : "text-foreground/70 hover:bg-muted/50 hover:text-foreground",
                            isHidden ? "opacity-50" : "opacity-100",
                          )}
                          aria-label={`${isHidden ? 'Show' : 'Hide'} ${ring.name}`}
                          title={`${isHidden ? 'Show' : 'Hide'} ${ring.name}`}
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="right">{isHidden ? `Show ${ring.name}` : `Hide ${ring.name}`}</TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          ) : null}

          {useSplitVerticalRails ? (
            <div className="absolute inset-x-0 top-0 z-20 flex flex-row gap-1 px-2 pt-1">
              <div
                ref={horizontalRailRef}
                className="pointer-events-auto flex flex-row gap-1 overflow-x-auto rounded-lg border border-border/50 bg-background/80 p-1 shadow-lg backdrop-blur-sm scrollbar-none"
                onPointerDown={handleHorizontalRailPointerDown}
                onPointerMove={handleHorizontalRailPointerMove}
                onPointerUp={handleHorizontalRailPointerUp}
                onPointerCancel={handleHorizontalRailPointerUp}
              >
                {railRings.map((ring, index) => {
                  const isHidden = hiddenRings.includes(ring.id);
                  const isSelected = !isHidden && (selectedRing === ring.id || (!selectedRing && index === 0));
                  const Icon = getRailIcon(ring.id);
                  return (
                    <Tooltip key={`ring-rail-h-${ring.id}`}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => handleRailPress(ring.id, index)}
                          className={cn(
                            "flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors duration-150",
                            isSelected
                              ? "bg-primary/18 text-primary"
                              : "text-foreground/70 hover:bg-muted/50 hover:text-foreground",
                            isHidden ? "opacity-50" : "opacity-100",
                          )}
                          aria-label={`${isHidden ? 'Show' : 'Hide'} ${ring.name}`}
                          title={`${isHidden ? 'Show' : 'Hide'} ${ring.name}`}
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">{isHidden ? `Show ${ring.name}` : `Hide ${ring.name}`}</TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div
            ref={canvasRef}
            className="relative flex h-full w-full min-w-0 flex-1 items-center justify-center overflow-hidden"
            onMouseEnter={activateCycleOverlay}
            onMouseLeave={scheduleHideCycleOverlay}
            onFocusCapture={activateCycleOverlay}
            onBlurCapture={scheduleHideCycleOverlay}
          >
          <svg 
            viewBox={`0 0 ${size} ${size}`} 
            className="cursor-grab active:cursor-grabbing touch-none select-none"
            style={{
              width: '100%',
              height: '100%',
              maxWidth: size,
              maxHeight: size,
              aspectRatio: '1 / 1',
              minWidth: 0,
              minHeight: 0,
              transform: effectiveDisplayMode === 'single' ? 'scale(0.965)' : 'scale(1)',
              transition: 'transform 360ms cubic-bezier(0.22, 1, 0.36, 1)',
            }}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
          {/* ...SVG content... */}
        <defs>
          {/* Gradient definitions for moon ring segments based on lunar illumination */}
          {safeRings.filter(r => r.id === 'moons' || (r.name ?? '').includes('Moon')).map(ring => 
            Array.from({ length: ring.stages }).map((_, monthIndex) => {
              // Calculate illumination for each day within this moon (28 days)
              const startDay = monthIndex * 28 + 1;
              const illuminations = Array.from({ length: 28 }, (_, d) => 
                moonIlluminations[Math.min(startDay + d - 1, 363)] || 0
              );
              
              // Create gradient stops based on illumination throughout the month
              // Sample at key points: start, 1/4, 1/2, 3/4, end
              const samples = [0, 7, 14, 21, 27].map(d => illuminations[d]);
              
              return (
                <linearGradient
                  key={`moon-illum-${monthIndex}`}
                  id={`moon-illum-${monthIndex}`}
                  gradientUnits="objectBoundingBox"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  {samples.map((illum, i) => {
                    // Interpolate between dark blue (new moon) and bright silver (full moon)
                    const lightness = 15 + illum * 70; // 15% to 85%
                    const saturation = 30 - illum * 20; // 30% to 10%
                    return (
                      <stop
                        key={i}
                        offset={`${(i / (samples.length - 1)) * 100}%`}
                        stopColor={`hsl(220 ${saturation}% ${lightness}%)`}
                      />
                    );
                  })}
                </linearGradient>
              );
            })
          )}
          {isSingleRing && (
            <>
              <radialGradient id="single-ring-halo" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.08" />
                <stop offset="55%" stopColor="hsl(var(--accent))" stopOpacity="0.35" />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.75" />
              </radialGradient>
              <linearGradient id="single-ring-band" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.6" />
                <stop offset="50%" stopColor="hsl(var(--accent))" stopOpacity="0.4" />
                <stop offset="100%" stopColor="hsl(var(--cosmic-star))" stopOpacity="0.6" />
              </linearGradient>
              <linearGradient id="single-ring-trace" x1="0%" y1="100%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity="0.15" />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.5" />
              </linearGradient>
            </>
          )}
        </defs>
        
        {!isSingleRing && (
          <circle
            cx={center}
            cy={center}
            r={center - 5}
            fill="none"
            stroke="hsl(var(--primary) / 0.2)"
            strokeWidth="1"
            strokeDasharray="4 4"
          />
        )}

        {displayedRings.map((ring, ringIndex) => {
          const outerR = center - (ringIndex * (ringWidth + ringGap)) - 10;
          const innerR = outerR - ringWidth;
          
          if (innerR < innerRadius) return null;
          
          const segmentGap = ring.stages > 30 ? 0.5 : 1.5;
          const segmentAngle = (360 - ring.stages * segmentGap) / ring.stages;
          const activeStage = getActiveStage(ring.rotation, ring.stages);
          const isSelected = selectedRing === ring.id;
          const isMoonRing = ring.id === 'moons' || ((ring.name ?? '').includes('Moon'));
          const iconOnlyRing = effectiveDisplayMode === 'multi' && ringHasIconLabels(ring);
          
          return (
            <g 
              key={ring.id}
              style={{ 
                transform: `rotate(${ring.rotation}deg)`,
                transformOrigin: 'center',
                transition: dragging ? 'none' : 'transform 0.3s ease-out'
              }}
              onMouseDown={(e) => handleMouseDown(e, ring.id)}
            >
              {Array.from({ length: ring.stages }).map((_, stageIndex) => {
                const startAngle = stageIndex * (segmentAngle + segmentGap);
                const endAngle = startAngle + segmentAngle;
                const isActive = stageIndex === activeStage;
                const midAngle = startAngle + segmentAngle / 2;
                const midRad = (midAngle + ZERO_REFERENCE_OFFSET_DEG) * (Math.PI / 180);
                const labelRadius = (innerR + outerR) / 2;
                const labelX = center + labelRadius * Math.cos(midRad);
                const labelY = center + labelRadius * Math.sin(midRad);
                const colorVar = ring.colors[stageIndex % ring.colors.length];
                const stageLabel = String(ring.labels[stageIndex] || (stageIndex + 1));
                const isStageIcon = ICON_LABEL_REGEX.test(stageLabel);
                const iconFontSize = ring.stages > 40 ? '8' : ring.stages > 28 ? '10' : ring.stages > 20 ? '11' : ring.stages > 13 ? '12' : '13';
                
                // For moon ring: subdivide into 7 sub-arcs (4 days each) showing illumination
                if (isMoonRing) {
                  const subSegments = 7; // 4 days per sub-segment
                  const subAngle = segmentAngle / subSegments;
                  const baseDay = stageIndex * 28 + 1;
                  
                  return (
                    <g key={stageIndex} className="cursor-pointer" onClick={(e) => handleStageClick(ring.id, stageIndex, e)}>
                      {Array.from({ length: subSegments }).map((_, subIndex) => {
                        const subStart = startAngle + subIndex * subAngle;
                        const subEnd = subStart + subAngle;
                        // Get illumination for the middle day of this sub-segment
                        const dayIndex = baseDay + Math.floor(subIndex * 4 + 2) - 1;
                        const illum = moonIlluminations[Math.min(dayIndex, 363)] || 0;
                        // Dark blue (new moon) to silver/white (full moon)
                        const lightness = 15 + illum * 70;
                        const saturation = 30 - illum * 20;
                        
                        return (
                          <path
                            key={subIndex}
                            d={createArcPath(subStart, subEnd, innerR, outerR)}
                            fill={isSingleRing || iconOnlyRing ? 'transparent' : `hsl(220 ${saturation}% ${lightness}%)`}
                            stroke="none"
                          />
                        );
                      })}
                      {/* Overlay border for the full segment */}
                      <path
                        d={createArcPath(startAngle, endAngle, innerR, outerR)}
                        fill="none"
                        stroke={isSingleRing || iconOnlyRing ? 'transparent' : (isSelected ? 'hsl(var(--primary))' : isActive ? 'hsl(var(--primary))' : 'hsl(220 30% 40%)')}
                        strokeWidth={isSingleRing || iconOnlyRing ? 0 : (isActive ? 2 : isSelected ? 1.5 : 0.5)}
                        className="transition-all duration-200"
                      />
                      {!isSingleRing && (
                        <text
                          x={labelX}
                          y={labelY}
                          textAnchor="middle"
                          dominantBaseline="central"
                          fill={isActive ? 'hsl(var(--primary))' : 'hsl(var(--foreground))'}
                          fontSize={isStageIcon ? '12' : '9'}
                          fontWeight="600"
                          className="pointer-events-none font-display select-none"
                          style={{ transform: `rotate(${-ring.rotation}deg)`, transformOrigin: `${labelX}px ${labelY}px` }}
                        >
                          {stageLabel}
                        </text>
                      )}
                    </g>
                  );
                }
                
                // Non-moon rings: solid colors
                const fillColor = isActive ? `hsl(${colorVar})` : `hsl(${colorVar} / 0.25)`;
                
                return (
                  <g key={stageIndex} className="cursor-pointer">
                    <path
                      d={createArcPath(startAngle, endAngle, innerR, outerR)}
                      fill={isSingleRing || iconOnlyRing ? 'transparent' : fillColor}
                      stroke={isSingleRing || iconOnlyRing ? 'transparent' : (isSelected ? 'hsl(var(--primary))' : `hsl(${colorVar})`)}
                      strokeWidth={isSingleRing || iconOnlyRing ? 0 : (isActive ? 2 : isSelected ? 1.5 : 0.5)}
                      className="transition-all duration-200"
                      onClick={(e) => handleStageClick(ring.id, stageIndex, e)}
                    />
                    
                    {!isSingleRing && ring.stages <= 52 && (
                      <text
                        x={labelX}
                        y={labelY}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fill={isActive ? 'hsl(var(--primary-foreground))' : 'hsl(var(--foreground))'}
                        fontSize={isStageIcon ? iconFontSize : (ring.stages > 40 ? '5' : ring.stages > 28 ? '6' : ring.stages > 20 ? '7' : ring.stages > 13 ? '8' : '9')}
                        fontWeight={isStageIcon ? '600' : '500'}
                        className="pointer-events-none font-display select-none"
                        style={{ transform: `rotate(${-ring.rotation}deg)`, transformOrigin: `${labelX}px ${labelY}px` }}
                      >
                        {stageLabel}
                      </text>
                    )}
                  </g>
                );
              })}

              {!isSingleRing && !iconOnlyRing && (
                <g className="pointer-events-none">
                  {Array.from({ length: ring.stages }).map((_, stageIndex) => {
                    const startAngle = stageIndex * (segmentAngle + segmentGap);
                    const midAngle = startAngle + segmentAngle / 2;
                    const midRad = (midAngle + ZERO_REFERENCE_OFFSET_DEG) * (Math.PI / 180);
                    const markerInner = outerR + 4;
                    const markerOuter = outerR + 13;
                    const markerRadius = outerR + 19;
                    const labelRadius = outerR + 30;
                    const markerX = center + markerRadius * Math.cos(midRad);
                    const markerY = center + markerRadius * Math.sin(midRad);
                    const stageLabel = String(ring.labels[stageIndex] || stageIndex + 1);
                    const compactLabel = stageLabel.length > 3 ? stageLabel.slice(0, 3).toUpperCase() : stageLabel;
                    const isActive = stageIndex === activeStage;
                    const colorVar = ring.colors[stageIndex % ring.colors.length];
                    const labelX = center + labelRadius * Math.cos(midRad);
                    const labelY = center + labelRadius * Math.sin(midRad);

                    return (
                      <g key={`single-marker-${stageIndex}`}>
                        <line
                          x1={center + markerInner * Math.cos(midRad)}
                          y1={center + markerInner * Math.sin(midRad)}
                          x2={center + markerOuter * Math.cos(midRad)}
                          y2={center + markerOuter * Math.sin(midRad)}
                          stroke={isActive ? 'hsl(var(--primary))' : `hsl(${colorVar} / 0.65)`}
                          strokeWidth={isActive ? 2 : 1}
                        />
                        <circle
                          cx={markerX}
                          cy={markerY}
                          r={isActive ? 3.8 : 2.1}
                          fill={isActive ? 'hsl(var(--primary))' : `hsl(${colorVar})`}
                          opacity={isActive ? 1 : 0.8}
                        />
                        {ring.stages <= 20 && (
                          <text
                            x={labelX}
                            y={labelY}
                            textAnchor="middle"
                            dominantBaseline="central"
                            fill={isActive ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))'}
                            fontSize={ring.stages <= 13 ? '8' : '6'}
                            fontWeight={isActive ? '700' : '500'}
                            className="font-display select-none"
                            style={{ transform: `rotate(${-ring.rotation}deg)`, transformOrigin: `${labelX}px ${labelY}px` }}
                          >
                            {compactLabel}
                          </text>
                        )}
                      </g>
                    );
                  })}
                </g>
              )}
              
              {/* Drift day indicator for days ring - Day Out of Time */}
              {!isSingleRing && !iconOnlyRing && (ring.id === 'days' || ring.stages === 28) && (
                <g className="pointer-events-none">
                  {/* Small golden segment after day 28 representing drift/Day Out of Time */}
                  <path
                    d={createArcPath(
                      ring.stages * (segmentAngle + segmentGap) - segmentGap,
                      360,
                      innerR,
                      outerR
                    )}
                    fill="hsl(45 80% 50% / 0.7)"
                    stroke="hsl(45 90% 60%)"
                    strokeWidth="1"
                  />
                  {/* Star symbol for Day Out of Time */}
                  <text
                    x={center + (innerR + outerR) / 2 * Math.cos((ZERO_REFERENCE_OFFSET_DEG + (360 - segmentGap / 2)) * Math.PI / 180)}
                    y={center + (innerR + outerR) / 2 * Math.sin((ZERO_REFERENCE_OFFSET_DEG + (360 - segmentGap / 2)) * Math.PI / 180)}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="hsl(45 90% 20%)"
                    fontSize="6"
                    className="font-display"
                    style={{ transform: `rotate(${-ring.rotation}deg)`, transformOrigin: `${center}px ${center}px` }}
                  >
                    ✧
                  </text>
                </g>
              )}
              
              {/* Drift gap indicator for moon ring - shows 1+ day solar year difference */}
              {!isSingleRing && !iconOnlyRing && isMoonRing && (
                <g className="pointer-events-none">
                  {/* Gap after last moon segment (364 days vs 365.25 solar days) */}
                  <path
                    d={createArcPath(
                      ring.stages * (segmentAngle + segmentGap) - segmentGap,
                      360,
                      innerR,
                      outerR
                    )}
                    fill="hsl(45 80% 50% / 0.6)"
                    stroke="hsl(45 90% 60%)"
                    strokeWidth="1"
                    strokeDasharray="2 2"
                  />
                  {/* Drift label */}
                  <text
                    x={center + (innerR + outerR) / 2 * Math.cos((ZERO_REFERENCE_OFFSET_DEG + (360 - segmentGap / 2)) * Math.PI / 180)}
                    y={center + (innerR + outerR) / 2 * Math.sin((ZERO_REFERENCE_OFFSET_DEG + (360 - segmentGap / 2)) * Math.PI / 180)}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="hsl(45 90% 70%)"
                    fontSize="6"
                    fontWeight="bold"
                    className="font-display"
                    style={{ transform: `rotate(${-ring.rotation}deg)`, transformOrigin: `${center}px ${center}px` }}
                  >
                    +1
                  </text>
                </g>
              )}
              
              {!isSingleRing && !iconOnlyRing && (
                <circle
                  cx={center}
                  cy={center + outerR - 3}
                  r={2.5}
                  fill="hsl(var(--primary))"
                  className="pointer-events-none"
                  style={{ transform: `rotate(${-ring.rotation}deg)`, transformOrigin: `${center}px ${center}px` }}
                />
              )}
            </g>
          );
        })}
        
        <circle cx={center} cy={center} r={innerRadius - 5} fill="hsl(var(--background))" stroke="hsl(var(--border))" strokeWidth="1" />
        
        {/* Moon phase in center */}
        <foreignObject x={center - 30} y={center - 30} width="60" height="60">
          <div className="w-full h-full flex items-center justify-center">
            <MoonPhase gregorianDate={gregorianDate} size={50} showLabel={false} />
          </div>
        </foreignObject>
        
        <polygon points={`${center},${size - 16} ${center - 5},${size - 8} ${center + 5},${size - 8}`} fill="hsl(var(--primary))" />
          </svg>

          {selectedVisibleRing && (
            <div
              className={cn(
                "pointer-events-none absolute inset-0 transition-all duration-300 ease-out",
                isSingleRing ? "opacity-100 scale-100" : "opacity-0 scale-95"
              )}
              aria-hidden={!isSingleRing}
            >
              <div
                className="absolute inset-0"
                style={{
                  transform: `rotate(${selectedVisibleRing.rotation}deg)`,
                  transformOrigin: 'center',
                  transition: dragging ? 'none' : 'transform 0.3s ease-out',
                }}
              >
                {singleRingDisplayLabels.map((displayLabel, index) => {
                  const angle = (index / Math.max(1, singleRingDisplayLabels.length)) * Math.PI * 2 - Math.PI / 2;
                  const radiusPx = Math.min(size * 0.34, 132);
                  const x = Math.cos(angle) * radiusPx;
                  const y = Math.sin(angle) * radiusPx;
                  return (
                    <div
                      key={`single-icon-${index}`}
                      className="absolute flex h-[36px] w-[36px] items-center justify-center text-primary/95"
                      style={{
                        left: `calc(50% + ${x}px - 18px)`,
                        top: `calc(50% + ${y}px - 18px)`,
                      }}
                    >
                      <span
                        className="pointer-events-none select-none text-[28px] leading-none"
                        style={{
                          transform: `rotate(${-selectedVisibleRing.rotation}deg)`,
                          transition: dragging ? 'none' : 'transform 0.3s ease-out',
                        }}
                      >
                        {displayLabel}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          </div>

        </div>
      {/* Save group dialog */}
      <Dialog open={showGroupDialog} onOpenChange={(open) => {
        setShowGroupDialog(open);
        if (!open) {
          setEditingGroupId(null);
          setGroupDraft({ name: '', description: '' });
          setIsCreatingGroup(false);
        }
      }}>
        <DialogContent className="bg-card/95 backdrop-blur border-border/60 max-w-[95vw] w-full sm:max-w-md p-3 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">{isCreatingGroup ? 'Create cycle group' : 'Edit cycle group'}</DialogTitle>
            <DialogDescription className="sr-only">
              Save or update a cycle group name and description.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 sm:space-y-4">
            <div className="space-y-1.5 sm:space-y-2">
              <Input
                placeholder="Group name"
                value={groupDraft.name}
                onChange={(e) => setGroupDraft((p) => ({ ...p, name: e.target.value }))}
                className="text-sm sm:text-base py-2 sm:py-2.5"
              />
            </div>
            <div className="space-y-1.5 sm:space-y-2">
              <Textarea
                placeholder="Description"
                value={groupDraft.description}
                onChange={(e) => setGroupDraft((p) => ({ ...p, description: e.target.value }))}
                className="text-sm sm:text-base min-h-[60px] sm:min-h-[80px] py-2 sm:py-2.5"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 mt-3 sm:mt-4">
            <Button variant="ghost" onClick={() => setShowGroupDialog(false)} className="text-sm sm:text-base">Cancel</Button>
            <Button
              disabled={!(groupDraft?.name ?? '').trim()}
              onClick={async () => {
                if (!editingGroupId) return;
                if (!(groupDraft?.name ?? '').trim()) return;
                try {
                  const group = {
                    id: editingGroupId,
                    name: (groupDraft?.name ?? '').trim(),
                    description: groupDraft.description.trim() || undefined,
                    rings: safeRings.map(r => ({ ...r })),
                    hiddenRings,
                    visibleRingCount,
                  } as CycleGroupData;
                  await saveCycleGroup(viewId, group);
                  const updated = await loadCycleGroups(viewId);
                  setCycleGroups(updated);
                  setSelectedGroupId(group.id);
                  try { saveSelectedGroupId(viewId, group.id); } catch { void 0; }
                  setGroupDraft({ name: '', description: '' });
                  setEditingGroupId(null);
                  setIsCreatingGroup(false);
                  setShowGroupDialog(false);
                  toast.success(isCreatingGroup ? 'Group created' : 'Group updated');
                } catch (err) {
                  toast.error('Failed to save group');
                }
              }}
              className="text-sm sm:text-base"
            >
              {isCreatingGroup ? 'Create group' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
